import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import encodeFormData from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeFormData";
import { useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { CUSTOMERS_TYPES_VALUE_PARTICULAR } from "@raiden/library/constants/customers";

/**
 * @typedef {object} ContactFormValuesCustomAttributes
 * @property {string[]} contracts
 * @property {import("../../../constants/contact").ContactTypeValue | ""} contactType
 * @property {string} contactDate
 * @property {import("../../../constants/contact").ContactTimeOfDayValue | ""} contactTimeOfDay
 */

/**
 * @typedef {object} ContactFormValues
 * @property {object} data
 * @property {string} data.first_name
 * @property {string} data.last_name
 * @property {string} data.phone_country
 * @property {string} data.phone_number
 * @property {string} data.email
 * @property {boolean} data.accept
 * @property {number} data.environment_id
 * @property {string} data.customer_type
 * @property {string} data.content
 * @property {ContactFormValuesCustomAttributes} data.custom_attributes
 */

/**
 * @param {object} params
 * @param {number} params.environment_id
 * @returns {ContactFormValues}
 */
function getDefaultValues({ environment_id }) {
  return {
    data: {
      first_name: "",
      last_name: "",
      phone_country: "FR",
      phone_number: "",
      email: "",
      accept: false,
      environment_id,
      customer_type: CUSTOMERS_TYPES_VALUE_PARTICULAR,
      content: "",
      custom_attributes: {
        contracts: [],
        contactType: "",
        contactDate: "",
        contactTimeOfDay: "",
      },
    },
  };
}

/**
 * @typedef {object} Props
 * @property {() => void} [onSuccess]
 * @property {boolean} [withFormTag]
 * @property {(params: {form: import("react-hook-form").UseFormReturn<ContactFormValues>, submit: () => void, isLoading: boolean}) => import("react").ReactElement} children
 *
 * @param {Props} props
 */
export default function ContactsCreateHandler({
  onSuccess,
  withFormTag = true,
  children,
}) {
  const intl = useIntl();

  const { bestEnvironment } = usePreferences();

  /** @type {import("react-hook-form").UseFormReturn<ContactFormValues>} */
  const form = useForm({
    defaultValues: getDefaultValues({
      environment_id: bestEnvironment?.id ?? -1,
    }),
  });
  const { handleSubmit } = form;

  const { request, isLoading, toastSuccess, toastError } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const submit = useCallback(
    (event) => {
      event.preventDefault();
      handleSubmit(async (values) => {
        if (!executeRecaptcha) {
          console.warn("Execute recaptcha not yet available");
          toastError({
            id: "invalid-recaptcha",
            status: "error",
            description: intl.formatMessage({
              defaultMessage: "Impossible de charger le module de sécurité",
            }),
            title: intl.formatMessage({
              defaultMessage: "Erreur",
            }),
            isClosable: true,
          });
          return;
        }

        const token = await executeRecaptcha("submit_form_quotation");

        request(
          generateApiUrl({
            id: "@api.customers.contacts.create",
          }),
          {
            method: "POST",
            body: encodeFormData({
              data: values.data,
              meta: {
                recaptcha_token: token,
              },
            }),
          },
          {
            inputMode: "formData",
          },
        )
          .then((response) => {
            toastSuccess({
              title: intl.formatMessage({
                defaultMessage:
                  "Les modifications ont bien été prises en compte.",
              }),
            });
            resetApiErrors();
            onSuccess?.();
          })
          .catch((response) => {
            resolveApiError({ response, form });
          });
      })();
    },
    [
      executeRecaptcha,
      form,
      handleSubmit,
      intl,
      onSuccess,
      request,
      resetApiErrors,
      resolveApiError,
      toastError,
      toastSuccess,
    ],
  );

  return (
    <FormProvider {...form}>
      <ConditionalWrapper
        hasWrapper={withFormTag}
        wrapper={(children) => <form onSubmit={submit}>{children}</form>}>
        {children({ form, submit, isLoading })}
      </ConditionalWrapper>
    </FormProvider>
  );
}
